import { VirtualizerOptions, useVirtualizer } from '@tanstack/react-virtual';
import {
  forwardRef,
  Fragment,
  HTMLProps,
  ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import styled, { css } from 'styled-components';
import { findScrollableParent, getDOMProps } from 'app/shared/utils/react';

export interface U21VirtualizerRef {
  scrollToIndex: (index: number) => Promise<void>;
  container: HTMLDivElement | null;
}

export interface U21VirtualizerProps extends Omit<
  HTMLProps<HTMLDivElement>,
  'children'
> {
  children: (index: number) => ReactNode;
  disabled?: boolean;
  count: number;
  estimateSize: VirtualizerOptions<any, any>['estimateSize'];
  gap?: number;
  getItemKey?: (index: number) => string;
  overscan?: number;
  onReady?: () => void;
}

const MAX_LOOPS = 20;

export const U21Virtualizer = forwardRef<
  U21VirtualizerRef,
  U21VirtualizerProps
>((props, ref) => {
  const {
    children,
    count,
    disabled,
    estimateSize,
    gap,
    getItemKey,
    overscan = 1,
    onReady,
    ...rest
  } = props;

  const [containerElement, setContainerElement] =
    useState<HTMLDivElement | null>(null);
  const [nonVirtualizedContainerElement, setNonVirtualizedContainerElement] =
    useState<HTMLDivElement | null>(null);
  const [scrollElement, setScrollElement] = useState<HTMLElement | null>(null);
  const [scrollMargin, setScrollMargin] = useState(0);

  // tracks whether we should render fallback
  const [fallback, setFallback] = useState(false);

  const currLoops = useRef(0);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const hasNotifiedReady = useRef(false);

  const virtualizerDisabled = count <= overscan || disabled || fallback;

  // Memoized ref callbacks to prevent re-renders
  const setVirtualizedContainerRef = useCallback(
    (el: HTMLDivElement | null) => {
      setContainerElement(el);
      containerRef.current = el;
    },
    [],
  );

  const setNonVirtualizedContainerRef = useCallback(
    (el: HTMLDivElement | null) => {
      setNonVirtualizedContainerElement(el);
      containerRef.current = el;
    },
    [],
  );

  useEffect(() => {
    if (virtualizerDisabled) {
      return undefined;
    }
    if (scrollElement) {
      setFallback(false);
      return undefined;
    }

    // it's possible that the scrollable element is not scrollable yet due to animations
    // so check every 100ms until we find it
    currLoops.current = 0;
    const interval = setInterval(() => {
      currLoops.current += 1;
      if (containerElement) {
        const newScrollElement = findScrollableParent(
          containerElement,
          'U21Page__Content',
        );
        setScrollElement(newScrollElement);
        if (newScrollElement) {
          setScrollMargin(
            containerElement.getBoundingClientRect().top -
              newScrollElement.getBoundingClientRect().top +
              newScrollElement.scrollTop,
          );
        }
      }
      if (currLoops.current > MAX_LOOPS) {
        clearInterval(interval);
        setFallback(true);
      }
    }, 100);
    return () => clearInterval(interval);
  }, [containerElement, scrollElement, virtualizerDisabled]);

  useEffect(() => {
    if (!containerElement || !scrollElement) {
      return undefined;
    }
    const resizeObserver = new ResizeObserver(() => {
      setScrollMargin(
        containerElement.getBoundingClientRect().top -
          scrollElement.getBoundingClientRect().top +
          scrollElement.scrollTop,
      );
    });
    resizeObserver.observe(scrollElement);
    return () => resizeObserver.disconnect();
  }, [containerElement, scrollElement]);

  const virtualizer = useVirtualizer({
    // disable virtualizer if scrollElement is not found since it relies on it
    enabled: !(virtualizerDisabled || !scrollElement),
    count,
    gap,
    getItemKey,
    getScrollElement: () => scrollElement,
    estimateSize,
    overscan,
    scrollMargin,
    initialOffset: () => {
      // set initialOffset to current scroll positio so it doesn't scroll to the top
      // https://github.com/TanStack/virtual/issues/1038
      return scrollElement?.scrollTop ?? 0;
    },
  });

  useEffect(() => {
    const isReady = virtualizerDisabled
      ? nonVirtualizedContainerElement
      : containerElement && scrollElement;

    if (isReady && onReady && !hasNotifiedReady.current) {
      hasNotifiedReady.current = true;
      onReady();
    }
  }, [
    containerElement,
    nonVirtualizedContainerElement,
    onReady,
    scrollElement,
    virtualizerDisabled,
  ]);

  useImperativeHandle(
    ref,
    () => ({
      scrollToIndex: (index: number): Promise<void> => {
        return new Promise((resolve) => {
          if (virtualizerDisabled) {
            if (containerRef.current) {
              const child = containerRef.current.children[index];

              if (child instanceof HTMLElement) {
                child.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }
            resolve();
          } else {
            if (!scrollElement) {
              resolve();
              return;
            }

            // Perform the scroll
            if (containerElement) {
              virtualizer.scrollToIndex(index, {
                align: 'start',
                behavior: 'auto',
              });
            }

            requestAnimationFrame(() => {
              resolve();
            });
          }
        });
      },
      container: containerRef.current,
    }),
    [scrollElement, virtualizerDisabled, containerElement, virtualizer],
  );

  if (virtualizerDisabled) {
    return (
      <Container $gap={gap} ref={setNonVirtualizedContainerRef}>
        {Array.from({ length: count }).map((_, index) => (
          <Fragment key={getItemKey ? getItemKey?.(index) : index}>
            {children(index)}
          </Fragment>
        ))}
      </Container>
    );
  }

  return (
    <VirtualizedContainer
      $height={virtualizer.getTotalSize()}
      ref={setVirtualizedContainerRef}
      {...getDOMProps(rest)}
    >
      {virtualizer.getVirtualItems().map((i) => {
        return (
          <VirtualElement
            $translateY={i.start - virtualizer.options.scrollMargin}
            key={i.key}
            data-index={i.index}
            ref={virtualizer.measureElement}
          >
            {children(i.index)}
          </VirtualElement>
        );
      })}
    </VirtualizedContainer>
  );
});

const Container = styled.div<{ $gap?: number }>`
  ${(props) =>
    props.$gap
      ? css`
          display: flex;
          flex-direction: column;
          gap: ${props.$gap}px;
        `
      : css``}
`;

const VirtualizedContainer = styled.div<{ $height: number }>`
  height: ${({ $height }) => $height}px;
  position: relative;
`;

const VirtualElement = styled.div<{ $translateY: number }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  transform: translateY(${({ $translateY }) => $translateY}px);
`;
